import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'
const plumLight = '#6B21A8'

// 24 curated emojis covering most common category needs.
// Grouped loosely so they feel intentional when rendered in a 6×4 grid.
const ICON_GRID = [
  '🛋️', '🪴', '🎨', '🔨', '📚', '🎵',
  '💊', '⚽', '🎮', '✂️', '💼', '📎',
  '🧰', '🎪', '🚲', '⛽', '💻', '🖨️',
  '📦', '🎁', '🌿', '🍷', '🎂', '⭐',
]

/**
 * AddCategoryInline
 * Shows a small emoji-grid + name input. Calls onAdded(newCategory) after DB insert.
 *
 * Props:
 *   categoryType: 'business' | 'personal' — which bucket the new category goes in
 *   variant: 'business' | 'personal' — color accent (teal for business, plum for personal)
 *   onAdded: (newCategory) => void
 *   onCancel: () => void
 */
export default function AddCategoryInline({ categoryType = 'business', variant = 'business', onAdded, onCancel }) {
  const { t } = useTranslation()
  const [icon, setIcon] = useState(ICON_GRID[0])
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const accent = variant === 'personal' ? plumLight : teal

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError(t('add_category.error_required'))
      return
    }
    if (trimmed.length > 40) {
      setError(t('add_category.error_too_long'))
      return
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error(t('add_category.error_not_signed_in'))

      // Check for duplicate (case-insensitive within the same category_type)
      const { data: existing } = await supabase
        .from('categories')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('category_type', categoryType)
        .ilike('name', trimmed)

      if (existing && existing.length > 0) {
        const bucket = categoryType === 'personal' ? t('add_category.bucket_personal') : t('add_category.bucket_business')
        setError(t('add_category.error_duplicate', { name: trimmed, bucket }))
        setSaving(false)
        return
      }

      // Compute a sort_order that puts this after all existing user-added ones
      const { data: lastInType } = await supabase
        .from('categories')
        .select('sort_order')
        .eq('user_id', user.id)
        .eq('category_type', categoryType)
        .order('sort_order', { ascending: false })
        .limit(1)

      const nextSortOrder = (lastInType?.[0]?.sort_order || 0) + 1

      const { data: newCat, error: insertErr } = await supabase
        .from('categories')
        .insert({
          user_id: user.id,
          name: trimmed,
          icon,
          category_type: categoryType,
          is_default: false, // user-created → always editable/deletable
          sort_order: nextSortOrder,
        })
        .select()
        .single()

      if (insertErr) throw insertErr

      onAdded?.(newCat)
    } catch (err) {
      setError(err.message || t('add_category.error_generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ ...styles.box, borderColor: accent }}>
      <div style={styles.label}>{t('add_category.new_category_label')}</div>

      {/* Icon grid */}
      <div style={styles.iconGrid}>
        {ICON_GRID.map(emoji => (
          <button
            key={emoji}
            type="button"
            style={{
              ...styles.iconBtn,
              ...(icon === emoji ? { ...styles.iconBtnActive, borderColor: accent } : {}),
            }}
            onClick={() => setIcon(emoji)}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Name input */}
      <input
        type="text"
        style={styles.input}
        placeholder={t('add_category.placeholder')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); handleSave() }
          if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
        }}
        maxLength={40}
        autoFocus
      />

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.actions}>
        <button
          type="button"
          style={styles.btnCancel}
          onClick={onCancel}
          disabled={saving}
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          style={{ ...styles.btnSave, background: accent, opacity: (!name.trim() || saving) ? 0.5 : 1 }}
          onClick={handleSave}
          disabled={!name.trim() || saving}
        >
          {saving ? t('add_category.adding') : t('add_category.add_cta', { icon, name: name.trim() || t('add_category.default_name') })}
        </button>
      </div>
    </div>
  )
}

const styles = {
  box: {
    background: '#F9FAFB',
    border: '2px solid #E5E7EB',
    borderRadius: '12px',
    padding: '14px',
    marginTop: '8px',
    marginBottom: '14px',
  },
  label: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '10px',
  },
  iconGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '6px',
    marginBottom: '12px',
  },
  iconBtn: {
    aspectRatio: '1/1',
    background: '#fff',
    border: '1.5px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'all 0.1s',
  },
  iconBtnActive: {
    background: '#fff',
    borderWidth: '2.5px',
    transform: 'scale(1.05)',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1.5px solid #E5E7EB',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    background: '#fff',
  },
  error: {
    background: '#FEE2E2',
    color: '#991B1B',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: 500,
    marginTop: '10px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  btnCancel: {
    flex: '0 0 auto',
    padding: '9px 16px',
    background: '#fff',
    color: '#6B7280',
    border: '1.5px solid #E5E7EB',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnSave: {
    flex: 1,
    padding: '9px 16px',
    color: '#fff',
    border: 'none',
    borderRadius: '100px',
    fontSize: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
}
